import { pdfjs } from '@/utils/pdf/pdfConfig'

const MAX_DIMENSION = 3500

/**
 * Returns the number of pages in a PDF file.
 */
export const getPdfPageCount = async (pdfFile: File): Promise<number> => {
  const arrayBuffer = await pdfFile.arrayBuffer()
  const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise
  return pdf.numPages
}

interface ConvertPdfPageToPngOptionsI {
  pdfFile: File
  pageNumber: number
  maxDimension?: number
}

/**
 * Converts a specific page of a PDF file to a PNG image file.
 * The resulting image is scaled so the largest dimension is maxDimension (default 3500px)
 * while maintaining the original aspect ratio.
 */
export const convertPdfPageToPng = async ({
  pdfFile,
  pageNumber,
  maxDimension = MAX_DIMENSION,
}: ConvertPdfPageToPngOptionsI): Promise<File> => {
  // Load the PDF document
  const arrayBuffer = await pdfFile.arrayBuffer()
  const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise
  const page = await pdf.getPage(pageNumber)

  // Get viewport at scale 1.0 to determine original dimensions
  const viewport = page.getViewport({ scale: 1.0 })

  // Calculate scale to fit maxDimension on the largest side
  const largestDimension = Math.max(viewport.width, viewport.height)
  const scale = maxDimension / largestDimension
  const scaledViewport = page.getViewport({ scale })

  // Create canvas and render the page
  const canvas = document.createElement('canvas')
  canvas.width = scaledViewport.width
  canvas.height = scaledViewport.height

  const context = canvas.getContext('2d')
  if (!context) {
    throw new Error('Failed to get canvas 2D context')
  }

  await page.render({
    canvasContext: context,
    viewport: scaledViewport,
  }).promise

  // Convert canvas to PNG blob
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error('Failed to convert canvas to blob'))
        }
      },
      'image/png',
      1.0
    )
  })

  // Create new File with .png extension
  const originalName = pdfFile.name.replace(/\.pdf$/i, '')
  const fileName = `${originalName}_page${pageNumber}.png`

  return new File([blob], fileName, { type: 'image/png' })
}
